import { useEffect, useState } from 'react'
import Papa from 'papaparse'

const ORDERS_CSV_URL = 'https://cdn.magicpatterns.com/uploads/tzhtyjKz4xJLBQ6CCnbRdk/nisolo_orders.csv'

export type OrderProduct = {
  name: string
  type: string
  condition: string
  units: number
  sales: number
  discount: number
  msrp: number
  sizes: string[]
  orders: number
}

export type OrderGroup = {
  label: string
  units: number
  sales: number
  orders: number
  discount: number
}

export type OrderPoint = {
  label: string
  key: string
  sales: number
  units: number
}

export type BarcodeActual = { units: number; sales: number; firstSold: string; lastSold: string }

export type NisoloOrderData = {
  byBarcode: Record<string, BarcodeActual>
  units: number
  sales: number
  orders: number
  discount: number
  msrpValue: number
  aur: number
  averageOrder: number
  cancelledUnits: number
  surpriseUnits: number
  products: OrderProduct[]
  channels: OrderGroup[]
  conditions: OrderGroup[]
  types: OrderGroup[]
  sizes: OrderGroup[]
  monthly: OrderPoint[]
  daily: OrderPoint[]
  firstOrder: string
  lastOrder: string
}

type CsvRow = Record<string, string>

const toNumber = (value: string | undefined) => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' })
const dayFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const fullFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

function accumulate(map: Map<string, OrderGroup>, label: string, units: number, sales: number, discount: number, orderKey: string, seen: Map<string, Set<string>>) {
  const existing = map.get(label) ?? { label, units: 0, sales: 0, orders: 0, discount: 0 }
  existing.units += units
  existing.sales += sales
  existing.discount += discount
  map.set(label, existing)

  const orders = seen.get(label) ?? new Set<string>()
  orders.add(orderKey)
  seen.set(label, orders)
}

function finalize(map: Map<string, OrderGroup>, seen: Map<string, Set<string>>) {
  return Array.from(map.values())
    .map((group) => ({ ...group, orders: seen.get(group.label)?.size ?? 0 }))
    .filter((group) => group.units > 0 || group.sales > 0)
    .sort((a, b) => b.sales - a.sales)
}

function buildOrders(rows: CsvRow[]): NisoloOrderData {
  let units = 0
  let sales = 0
  let discount = 0
  let msrpValue = 0
  let cancelledUnits = 0
  let surpriseUnits = 0

  const orderNumbers = new Set<string>()
  const productMap = new Map<string, OrderProduct>()
  const productOrders = new Map<string, Set<string>>()
  const channelMap = new Map<string, OrderGroup>()
  const channelOrders = new Map<string, Set<string>>()
  const conditionMap = new Map<string, OrderGroup>()
  const conditionOrders = new Map<string, Set<string>>()
  const typeMap = new Map<string, OrderGroup>()
  const typeOrders = new Map<string, Set<string>>()
  const sizeMap = new Map<string, OrderGroup>()
  const sizeOrders = new Map<string, Set<string>>()
  const monthMap = new Map<string, OrderPoint>()
  const dayMap = new Map<string, OrderPoint>()

  let earliest: Date | null = null
  let latest: Date | null = null
  const byBarcode: Record<string, BarcodeActual> = {}

  rows.forEach((row) => {
    const name = (row['product_name'] ?? '').trim()
    if (!name) return

    const quantity = toNumber(row['quantity'])
    // final_line_item_price is the price each unit sold at, so line revenue is
    // quantity multiplied by that unit price.
    const unitSalePrice = toNumber(row['final_line_item_price'])
    const lineSales = unitSalePrice * quantity
    const lineDiscount = toNumber(row['total_discount']) * quantity
    const orderNumber = (row['order_number'] ?? '').trim() || name
    const cancelled = Boolean((row['cancelled_at'] ?? '').trim()) || (row['order_status'] ?? '').toLowerCase().includes('cancel')

    if (cancelled) {
      cancelledUnits += quantity
      return
    }

    units += quantity
    sales += lineSales
    discount += lineDiscount
    msrpValue += toNumber(row['snap_msrp']) * quantity
    orderNumbers.add(orderNumber)
    if ((row['snap_is_surprise_box'] ?? '').trim().toLowerCase() === 'true') surpriseUnits += quantity

    const size = (row['snap_size'] ?? '').trim() || 'Unsized'
    const condition = (row['snap_condition'] ?? '').trim() || 'Unspecified'
    const type = (row['snap_product_type'] ?? '').trim() || 'Uncategorized'
    const channel = (row['channel'] ?? '').trim() || (row['source_name'] ?? '').trim() || 'Unknown'

    const product = productMap.get(name) ?? { name, type, condition, units: 0, sales: 0, discount: 0, msrp: 0, sizes: [], orders: 0 }
    product.units += quantity
    product.sales += lineSales
    product.discount += lineDiscount
    product.msrp = Math.max(product.msrp, toNumber(row['snap_msrp']))
    if (size && !product.sizes.includes(size)) product.sizes.push(size)
    productMap.set(name, product)
    const productOrderSet = productOrders.get(name) ?? new Set<string>()
    productOrderSet.add(orderNumber)
    productOrders.set(name, productOrderSet)

    accumulate(channelMap, channel, quantity, lineSales, lineDiscount, orderNumber, channelOrders)
    accumulate(conditionMap, condition, quantity, lineSales, lineDiscount, orderNumber, conditionOrders)
    accumulate(typeMap, type, quantity, lineSales, lineDiscount, orderNumber, typeOrders)
    accumulate(sizeMap, size, quantity, lineSales, lineDiscount, orderNumber, sizeOrders)

    const barcode = (row['snap_barcode'] ?? '').trim()
    const placed = new Date((row['placed_at'] ?? '').trim())

    if (barcode) {
      const existing = byBarcode[barcode] ?? { units: 0, sales: 0, firstSold: '', lastSold: '' }
      existing.units += quantity
      existing.sales += lineSales
      if (!Number.isNaN(placed.getTime())) {
        const stamp = placed.toISOString().slice(0, 10)
        if (!existing.firstSold || stamp < existing.firstSold) existing.firstSold = stamp
        if (!existing.lastSold || stamp > existing.lastSold) existing.lastSold = stamp
      }
      byBarcode[barcode] = existing
    }

    if (!Number.isNaN(placed.getTime())) {
      if (!earliest || placed < earliest) earliest = placed
      if (!latest || placed > latest) latest = placed

      const monthKey = `${placed.getUTCFullYear()}-${String(placed.getUTCMonth() + 1).padStart(2, '0')}`
      const month = monthMap.get(monthKey) ?? { key: monthKey, label: monthFormatter.format(placed), sales: 0, units: 0 }
      month.sales += lineSales
      month.units += quantity
      monthMap.set(monthKey, month)

      const dayKey = placed.toISOString().slice(0, 10)
      const day = dayMap.get(dayKey) ?? { key: dayKey, label: dayFormatter.format(placed), sales: 0, units: 0 }
      day.sales += lineSales
      day.units += quantity
      dayMap.set(dayKey, day)
    }
  })

  const products = Array.from(productMap.values())
    .map((product) => ({ ...product, orders: productOrders.get(product.name)?.size ?? 0 }))
    .sort((a, b) => b.sales - a.sales)

  return {
    byBarcode,
    units,
    sales,
    orders: orderNumbers.size,
    discount,
    msrpValue,
    aur: units > 0 ? sales / units : 0,
    averageOrder: orderNumbers.size > 0 ? sales / orderNumbers.size : 0,
    cancelledUnits,
    surpriseUnits,
    products,
    channels: finalize(channelMap, channelOrders),
    conditions: finalize(conditionMap, conditionOrders),
    types: finalize(typeMap, typeOrders),
    sizes: finalize(sizeMap, sizeOrders),
    monthly: Array.from(monthMap.values()).sort((a, b) => a.key.localeCompare(b.key)),
    daily: Array.from(dayMap.values()).sort((a, b) => a.key.localeCompare(b.key)),
    firstOrder: earliest ? fullFormatter.format(earliest) : '—',
    lastOrder: latest ? fullFormatter.format(latest) : '—',
  }
}

export function useNisoloOrders(enabled: boolean) {
  const [data, setData] = useState<NisoloOrderData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || data) return

    let cancelled = false
    setLoading(true)
    setError(null)

    fetch(ORDERS_CSV_URL)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load the Nisolo order file.')
        return response.text()
      })
      .then((text) => {
        if (cancelled) return
        const parsed = Papa.parse<CsvRow>(text, { header: true, skipEmptyLines: true })
        setData(buildOrders(parsed.data))
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [enabled, data])

  return { data, loading, error }
}
